import type { Perspective } from '@/core/models/perspective'
import type { LessonScale } from './teacher-assessment'

export type LessonPerspectiveKey = 'self' | 'teacher'

/** Member fields returned when an id on the kompetenzprofil is resolved. */
export type MemberViewModel = {
  readonly id: string
  readonly firstName?: string
  readonly lastName?: string
  readonly email?: string
  readonly canLogin: boolean
  readonly pending?: boolean
  readonly lastUpdatedAvatar?: string
}

export type KompetenzprofilLesson = {
  readonly lessonId: string
  readonly lessonImpulseId: string
  readonly lessonImpulseTitle: string | null
  readonly endedAt: string | null
  readonly teacherId: string
}

export type Kompetenzprofil = {
  readonly member: MemberViewModel
  readonly selfScale: LessonScale | null
  readonly teacherScale: LessonScale | null
  readonly lessons: ReadonlyArray<KompetenzprofilLesson>
  readonly teachers: ReadonlyArray<MemberViewModel>
}

export type IndikatorFeedback = {
  readonly senderId: string
  readonly value: number
}

export type IndikatorPerspective = {
  readonly key: LessonPerspectiveKey
  readonly numberOfFeedbacks: number
  readonly numberOfSenders: number
  readonly numberOfLessons: number
  readonly average: number | null
  readonly feedbacks: ReadonlyArray<IndikatorFeedback>
}

export type KompetenzPerspective = IndikatorPerspective & {
  readonly distribution: ReadonlyArray<{
    readonly value: number
    readonly count: number
  }>
}

export type KompetenzAnalysis = {
  readonly id: string
  readonly code: string
  readonly title: string
  readonly kompetenzbereich: { readonly key: string; readonly label: string }
  readonly perspectives: ReadonlyArray<KompetenzPerspective>
  readonly kompetenzstufen: ReadonlyArray<{
    readonly id: string
    readonly code: string
    readonly text: string
    readonly indikatoren: ReadonlyArray<{
      readonly id: string
      readonly text: string
      readonly perspectives: ReadonlyArray<IndikatorPerspective>
    }>
  }>
}

export type KompetenzprofilAnalysis = {
  readonly feedbackSenders: ReadonlyArray<MemberViewModel>
  readonly kompetenzen: ReadonlyArray<KompetenzAnalysis>
}

/** Lehrperson perspective. Self already exists as `SELF_PERSPECTIVE`. */
export const TEACHER_PERSPECTIVE: Perspective = {
  id: 'teacher',
  key: 'teacher',
  title: 'Lehrperson',
  nameLong: 'Lehrpersonen|Lehrperson|Lehrpersonen',
  nameShort: 'Lehrpersonen|Lehrperson|Lehrpersonen',
  icon: 'co_present',
  allowedMechanisms: ['app_user'],
}
